import { useEffect, useMemo, useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaginationState } from '@tanstack/react-table';
import { useSearchParams } from 'react-router-dom';
import Tour from 'reactour';
import { useSessionStorage } from 'usehooks-ts';

import FactsEditorTable from './FactsEditorTable';
import FactsEditorToolbar from './FactsEditorToolbar';
import { useSelectedFactsDb } from './hooks/useSelectedFactsDb';
import { APIBaseChronos } from '../../../../../api/hosts';
import useGetFetchConfig from '../../../../../api/useGetFetchConfig';
import FactDocViewer from '../../components/DocViewer/FactDocViewer';
import { SubToolbar } from '../components/TableToolbar/SubToolbar';
import { TableWrapper } from '../components/TableWrapper';
import { PageName, TrackingEvents } from '@/analytics/events';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetCaseDocTypes } from '@/api/queries/useGetCaseDocTypes';
import useFactsData from '@/api/queries/useGetFacts';
import { useGetKeyFacts } from '@/api/queries/useGetKeyFacts';
import { useGetPersons } from '@/api/queries/useGetPersons';
import { FilterPills } from '@/components/molecules/FilterPills';
import { CHRONOLOGY_STEPS } from '@/constants/tours';
import { useFactNaturalLanguageFilters } from '@/hooks/useNaturalLanguageFilters';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { FactFilters } from '@/types';
import { buildFactFilterPills } from '@/utils/buildFilterPills';
import { createPersonsMap } from '@/utils/filterLookups';
import { removeFilterValue } from '@/utils/filterUtils';

const FactsEditor = () => {
  const { fetchConfigGET } = useGetFetchConfig();

  // Query params
  const [searchParams, setSearchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  const pageNumber = searchParams.get('page') ?? '0';
  const docId = searchParams.get('docId');
  const factId = searchParams.get('factId');
  const personIds = useMemo(() => searchParams.getAll('personId'), [searchParams]);
  const themesParam = searchParams.get('themes');

  const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
  useEffect(() => {
    if (docId) {
      setIsDocViewerOpen(true);
    } else {
      setIsDocViewerOpen(false);
    }
  }, [docId]);

  const [keyFactFilterActive, setKeyFactFilterActive] = useState(false);
  const [disputedFactFilterActive, setDisputedFactFilterActive] = useState(false);

  // Initialise filters with sessionStorage persistence
  const defaultFilters: FactFilters = {
    docIds: [],
    excludedDocIds: [],
    relevance: [],
    classification: [],
    keyFacts: false,
    disputedFacts: false,
    startDate: null,
    endDate: null,
    runIds: [],
    personIds: [],
    documentTypes: [],
    themes: [],
    searchQuery: '',
  };

  const [filters, setFilters] = useSessionStorage<FactFilters>(`case-${caseId}-facts-table-filters`, defaultFilters);
  const [searchQueryInput, setSearchQueryInput] = useState(filters.searchQuery ?? '');

  const updateFilters = (newFilters: FactFilters | ((prev: FactFilters) => FactFilters)) => {
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    setSearchQueryInput('');
  };

  const [allFactIds, setAllFactIds] = useState<string[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: parseInt(pageNumber), pageSize: 50 });
  const [includedFactsCount, setIncludedFactsCount] = useState(0);
  const [createChronologyTourOpen, setCreateChronologyTourOpen] = useState(false);
  const [keyFactsSet, setKeyFactsSet] = useState(new Set<string>());
  const [totalPages, setTotalPages] = useState(0);
  const [isProcessingNL, setIsProcessingNL] = useState(false);

  const [areAllSelected, setAreAllSelected] = useState(false);

  // Custom hooks
  const { closeDocViewer } = useDocumentNavigation();
  const { db, doesCaseExist, getAllSelectedIds, selectMultiple, deselectMultiple, getAllSelectedFacts, deselectAll } =
    useSelectedFactsDb();

  const { buildFiltersWithNL, isProcessing: isProcessingNLFiltersQuery } = useFactNaturalLanguageFilters({
    caseId: caseId ?? '',
    onFiltersChange: (newFilters: FactFilters) => {
      updateFilters(newFilters);
      setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    },
    onSearchChange: (searchQuery: string) => {
      // Store keyword search in filters instead of overwriting the search input
      updateFilters((prev) => ({ ...prev, searchQuery: searchQuery }));
    },
    onKeyFactsToggle: (active: boolean) => {
      if (active && !keyFactFilterActive) {
        handleKeyFactFilterToggle();
      }
    },
    onDisputedFactsToggle: (active: boolean) => {
      if (active && !disputedFactFilterActive) {
        handleDisputedFactFilterToggle();
      }
    },
  });

  const { data: docTypes } = useGetCaseDocTypes({ caseId: caseId ?? '' });
  const { data: allPersons } = useGetPersons({ caseId, paginated: false });

  // Data - facts, key facts, counts, docs, case (for runs)
  const {
    data: responseFacts,
    isLoading,
    isFetching: isFetchingFacts,
    refetch: refetchCaseFacts,
  } = useFactsData({
    docId: null,
    caseId,
    searchQuery: filters.searchQuery,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filters,
  });

  const { data: keyFactsData, isLoading: isLoadingKeyFacts } = useGetKeyFacts(caseId);
  const { data: caseData } = useGetCase(caseId ?? '');

  // Handlers
  const handleToggleAll = async ({ checked: toggleValue }: { checked: boolean }): Promise<void> => {
    if (!caseId) throw new Error('No caseId found');

    trackCustomEvent(toggleValue ? TrackingEvents.ToggleAllFactsOn : TrackingEvents.ToggleAllFactsOff, { caseId });

    if (toggleValue) {
      await selectMultiple(caseId, allFactIds);

      // Recalculate the count from the database to ensure accuracy
      const selectedIds = await getAllSelectedIds(caseId);
      setIncludedFactsCount(selectedIds.length);
      checkIfAllSelected();
    } else {
      // Deselect all facts for this case
      // await deselectAll(caseId);
      await deselectMultiple(caseId, allFactIds);
      const selectedIds = await getAllSelectedIds(caseId);
      setIncludedFactsCount(selectedIds.length);

      checkIfAllSelected();
    }
  };

  // this function checks to see if the full list of ids returned by the query matches the selected id list in the db.
  // it should be called any time we make a change to the selected state.
  async function checkIfAllSelected() {
    if (!caseId) throw new Error('No caseId found');

    if (!allFactIds.length) return setAreAllSelected(false);

    const selectedFacts = await getAllSelectedFacts(caseId);
    const allSelected = allFactIds.every((i) => i in selectedFacts);

    setAreAllSelected(allSelected);
  }

  function deselectAllFacts() {
    if (!caseId) throw new Error('No caseId found');
    setAreAllSelected(false);
    setIncludedFactsCount(0);
    deselectAll(caseId);
  }

  const onSearchCall = async (searchValue?: string, useNaturalLanguage?: boolean) => {
    trackCustomEvent(TrackingEvents.SearchFacts, { caseId });

    const value = searchValue ?? searchQueryInput;

    if (useNaturalLanguage && value.trim()) {
      setIsProcessingNL(true);
      // Store the NL query separately to preserve it
      buildFiltersWithNL(value);
      closeDocViewer();
      setSearchQueryInput('');
      return;
    }

    // Regular search
    // Reset page to 0
    const newPaginationState = { pageIndex: 0, pageSize: pagination.pageSize };
    setPagination(newPaginationState);

    // Close the doc viewer if it is open
    closeDocViewer();
    updateFilters((prev) => ({ ...prev, searchQuery: value }));
    setSearchQueryInput(value);
  };

  const clearSearch = async () => {
    setSearchQueryInput('');
    updateFilters((prev) => ({ ...prev, searchQuery: '' }));
    setIsProcessingNL(false);
    await new Promise((resolve) => setTimeout(resolve, 0));
  };

  useEffect(() => {
    if (!isProcessingNLFiltersQuery) {
      setIsProcessingNL(false);
    }
  }, [isProcessingNLFiltersQuery]);

  const handleRemoveFilter = (filterKey: string, value?: any) => {
    if (filterKey === 'searchQuery') {
      setSearchQueryInput('');
    }
    updateFilters((prev) => removeFilterValue(prev, filterKey as keyof FactFilters, value));

    // Reset pagination when filters change
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  const handleKeyFactFilterToggle = async () => {
    // If keyFactFilterActive is currently true, then remove the event_id filter from the filters
    goToPage(0);
    if (keyFactFilterActive) {
      searchParams.delete('viewKeyFacts');
      setSearchParams(searchParams, { replace: true });

      const newFilters = { ...filters, keyFacts: false };
      updateFilters(newFilters);
    } else {
      trackCustomEvent(TrackingEvents.ToggleKeyFacts, { caseId });
      // If keyFactFilterActive is currently false, then fetch key fact event_ids and add to filters
      searchParams.set('viewKeyFacts', 'true');
      setSearchParams(searchParams, { replace: true });

      const newFilters = { ...filters, keyFacts: true };
      updateFilters(newFilters);
    }
  };

  const handleDisputedFactFilterToggle = async () => {
    goToPage(0);
    if (disputedFactFilterActive) {
      searchParams.delete('viewDisputedFacts');
      setSearchParams(searchParams, { replace: true });

      const newFilters = { ...filters, disputedFacts: false };
      updateFilters(newFilters);
    } else {
      trackCustomEvent(TrackingEvents.ToggleDisputedFacts, { caseId });
      searchParams.set('viewDisputedFacts', 'true');
      setSearchParams(searchParams, { replace: true });

      const newFilters = { ...filters, disputedFacts: true };
      updateFilters(newFilters);
    }
  };

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pagination.pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);

  const handlePageSizeChange = (newPageSize: number) => {
    const newPaginationState = { pageIndex: 0, pageSize: newPageSize };
    setPagination(newPaginationState);
  };

  const handleClearAllFilters = () => {
    clearFilters();

    // Clear URL params for key facts and disputed facts
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('viewKeyFacts');
    newParams.delete('viewDisputedFacts');
    newParams.delete('themes');
    // Remove all personId params
    const allKeys = Array.from(newParams.keys());
    allKeys.forEach((key) => {
      if (key === 'personId') {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams, { replace: true });

    // Reset pagination to first page
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  // useEffects
  // Update key facts set when key facts data changes
  useEffect(() => {
    if (!keyFactsData) return;
    setKeyFactsSet(new Set(keyFactsData.map((fact) => fact.id)));
  }, [keyFactsData]);

  // Handle key facts filter from URL (only when URL changes)
  useEffect(() => {
    if (searchParams.get('viewKeyFacts') === 'true') {
      setKeyFactFilterActive(true);
      updateFilters((prevFilters) => ({ ...prevFilters, keyFacts: true }));
    }
  }, [searchParams]);

  useEffect(() => {
    // Check the search params for viewDisputedFacts and if so, then set key fact filters
    if (searchParams.get('viewDisputedFacts') === 'true') {
      setDisputedFactFilterActive(true);
      updateFilters((prevFilters) => ({ ...prevFilters, disputedFacts: true }));
    }
  }, [searchParams]);

  // Sync filter active states with local state values
  useEffect(() => {
    setKeyFactFilterActive(Boolean(filters.keyFacts));
    setDisputedFactFilterActive(Boolean(filters.disputedFacts));
  }, [filters.keyFacts, filters.disputedFacts]);

  // Handle themes filter from URL
  useEffect(() => {
    if (themesParam) {
      updateFilters((prevFilters) => ({ ...prevFilters, themes: [themesParam] }));
    }
  }, [themesParam]);

  // Handle clear search
  useEffect(() => {
    if (filters.searchQuery === '') {
      setAllFactIds([]);
    }
  }, [filters.searchQuery, setAllFactIds]);

  // Handle if we have factIds in the response
  useEffect(() => {
    if (responseFacts) {
      //If result has come from search then we have the factIds in the response
      if (responseFacts.factIds) {
        setAllFactIds(responseFacts.factIds);
      }
      setTotalPages(responseFacts.pageCount);
    }
  }, [responseFacts]);

  // Load initial selected facts count from IndexedDB
  useEffect(() => {
    async function loadSelectedCount() {
      if (!caseId || !db) return;

      // first, see if the case exists at all
      const caseExistsInLocalDb = await doesCaseExist(caseId);

      if (caseExistsInLocalDb) {
        const selectedIds = await getAllSelectedIds(caseId);
        setIncludedFactsCount(selectedIds.length);
        checkIfAllSelected();
        return;
      }

      // fetch from server first
      const res = await fetch(`${APIBaseChronos}/api/case/${caseId}/included-facts`, fetchConfigGET);

      if (!res.ok) {
        // error fetching included facts, just set the case in the db and start from fresh
        // can just return in this case as there's nothing to do
        return;
      }

      const currentlySelectedIds = await res.json();
      selectMultiple(caseId, currentlySelectedIds);
      setIncludedFactsCount(currentlySelectedIds.length);
      checkIfAllSelected();
    }

    loadSelectedCount();
  }, [db, responseFacts, caseId, allFactIds]);

  // Start chronology tour from summary page
  useEffect(() => {
    // Check if explore = true is in search params
    if (isLoading || isFetchingFacts) return;
    if (searchParams.get('chronologyTour') === 'true') {
      setCreateChronologyTourOpen(true);

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('chronologyTour');
      // Update the URL without the 'chronologyTour' parameter
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, isLoading, isFetchingFacts]);

  // Memoize person IDs mapping to prevent unnecessary recalculations
  const mappedPersonIds = useMemo(() => {
    if (!allPersons || !personIds || personIds.length === 0) return [];

    return personIds
      .map((id) => {
        const person = allPersons.find((person) => person.id === id);
        if (!person) return null;

        return {
          label: person.name,
          value: id,
        };
      })
      .filter(Boolean);
  }, [personIds, allPersons]);

  // Handle personId filter from URL
  useEffect(() => {
    if (!allPersons) return;

    if (personIds && personIds.length > 0) {
      // Check if we need to clean up invalid person IDs from URL
      const invalidPersonIds = personIds.filter((id) => !allPersons.some((person) => person.id === id));

      if (invalidPersonIds.length > 0) {
        const newSearchParams = new URLSearchParams(searchParams);
        invalidPersonIds.forEach((id) => {
          newSearchParams.delete('personId', id);
        });
        setSearchParams(newSearchParams, { replace: true });
      }

      updateFilters((prevFilters) => ({
        ...prevFilters,
        personIds: personIds,
      }));
    }
  }, [personIds, allPersons, searchParams, setSearchParams, mappedPersonIds]);

  useEffect(() => {
    trackPageView(PageName.Facts);
  }, []);

  return (
    <div className="flex h-full min-w-full flex-col">
      <FactsEditorToolbar
        // Search
        globalFilter={searchQueryInput}
        clearSearch={clearSearch}
        setGlobalFilter={setSearchQueryInput}
        onSearchCall={onSearchCall}
        // Results
        refetchCaseFacts={refetchCaseFacts}
        resultsIncludedLength={includedFactsCount ?? 0}
        // Pagination
        canGetPrevPage={pagination.pageIndex > 0}
        canGetNextPage={pagination.pageIndex < totalPages - 1}
        prevPage={prevPage}
        nextPage={nextPage}
        goToPage={goToPage}
        currentPage={pagination.pageIndex}
        noOfPages={totalPages}
        pageSize={pagination.pageSize}
        onPageSizeChange={handlePageSizeChange}
        // Key facts
        loadingKeyFacts={isLoadingKeyFacts}
        hasKeyFacts={Boolean(keyFactsData && keyFactsData.length > 0)}
        keyFactFilterActive={keyFactFilterActive}
        handleKeyFactFilterToggle={handleKeyFactFilterToggle}
        // Disputed facts
        disputedFactFilterActive={disputedFactFilterActive}
        handleDisputedFactFilterToggle={handleDisputedFactFilterToggle}
        // -----------------
        activeFilters={filters}
        setFilters={updateFilters}
        caseRuns={caseData?.runs ?? []}
        allDocumentTypes={docTypes ?? []}
        allPersons={allPersons ?? []}
        onClusterComplete={() => {
          setIncludedFactsCount(0);
          refetchCaseFacts();
        }}
        processingNL={isProcessingNL}
        onNLComplete={() => setIsProcessingNL(false)}
      />
      <FilterPills
        pills={buildFactFilterPills(filters, handleRemoveFilter, createPersonsMap(allPersons))}
        onClearAll={handleClearAllFilters}
      />
      <SubToolbar
        entityType="facts"
        totalCount={responseFacts?.totalCount}
        totalUnfilteredCount={responseFacts?.totalUnfilteredCount}
        selectedCount={includedFactsCount}
        allRowsChecked={areAllSelected}
        handleToggleAll={handleToggleAll}
        deselectAll={deselectAllFacts}
      />
      <TableWrapper className="flex">
        {isLoading ? (
          <div className="flex h-full w-full items-center justify-center">
            <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" />
          </div>
        ) : (
          <div
            className={`h-full overflow-y-auto transition-all duration-300 ${isDocViewerOpen ? 'w-7/12' : 'w-full'}`}
          >
            <FactsEditorTable
              facts={responseFacts?.facts || []}
              keyFactIdSet={keyFactsSet}
              caseId={caseId || ''}
              setTotalNumberOfIncludedFacts={setIncludedFactsCount}
              checkIfAllSelected={checkIfAllSelected}
              includedFactsCount={includedFactsCount}
            />
          </div>
        )}
        <div
          className={`transition-all duration-300 ease-in-out ${
            isDocViewerOpen ? 'ml-2 w-5/12 opacity-100' : 'w-0 opacity-0'
          } mt-1 overflow-hidden rounded border-2 bg-white`}
        >
          <div
            className={`h-full w-full transition-transform duration-300 ease-in-out ${
              isDocViewerOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {isDocViewerOpen && docId && caseId && factId && (
              <FactDocViewer docId={docId} caseId={caseId} factId={factId} />
            )}
          </div>
        </div>
      </TableWrapper>
      <Tour
        startAt={0}
        steps={CHRONOLOGY_STEPS}
        isOpen={createChronologyTourOpen}
        onRequestClose={() => setCreateChronologyTourOpen(false)}
      />
    </div>
  );
};

export default FactsEditor;
